// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestForm.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestComposer.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat.test.tsx
import { type ParticipationTask } from "@mg/schemas/src/christo/participationTask";
import { useMemo, useState } from "react";

import {
  ApprovalRequestForm,
  type ApprovalCardHighlightTarget,
  type ApprovalRequestAction,
} from "./ApprovalRequestForm";

import {
  useAddProjectApprovalNote,
  useDeclineProjectInvitation,
  useParticipationTasks,
  useProjectReviews,
  useRecordProjectPosition,
} from "../../../utils/queries/askPunttProjects";
import {
  buildApprovalAssetsByVersionId,
  filterProjectFeedParticipationTasks,
} from "../utils/participationApprovalTasks";

/**
 * Shared-project-chat accessory: loads open participation tasks for this
 * project and submits approve / request-changes / not-needed via approval
 * events (does not send a chat message — keeps the agent asleep).
 *
 * Mounted above the text composer (outside the bordered input card) so it
 * reads as an in-feed decision card, not part of the message box.
 *
 * Always mounts ApprovalRequestForm (even when open tasks are empty) so a
 * post-deny optional-note step can stay sticky after the open-task list
 * clears.
 */
export function ApprovalRequestComposer({
  projectId,
  currentUserId,
  onCardHighlight,
}: {
  projectId: string;
  currentUserId?: string | null;
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
}) {
  const tasksQuery = useParticipationTasks({
    projectId,
    status: "open",
    enabled: !!projectId,
  });
  const reviewsQuery = useProjectReviews(projectId, {
    enabled: !!projectId,
  });
  const reviews = reviewsQuery.data?.reviews ?? [];
  // Scope to this project and drop rows that would only render the generic
  // "Review asset" placeholder. Critical: when reviews are IDLE/empty,
  // orphan/stale open tasks must yield zero cards — not two placeholders.
  const openTasks = useMemo(
    () =>
      filterProjectFeedParticipationTasks(
        tasksQuery.data?.tasks ?? [],
        projectId,
        reviews,
      ),
    [tasksQuery.data?.tasks, projectId, reviews],
  );
  // Keep denied cards mounted while the post-deny note step completes, even
  // after the open-task query drops them (mirrors HomeTaskList sticky rows).
  const [stickyPostDeny, setStickyPostDeny] = useState<
    Record<string, ParticipationTask>
  >({});
  const tasks = useMemo(() => {
    const byVersion = new Map(
      openTasks.map((task) => [task.versionId, task] as const),
    );
    for (const task of Object.values(stickyPostDeny)) {
      if (!byVersion.has(task.versionId)) {
        byVersion.set(task.versionId, task);
      }
    }
    return Array.from(byVersion.values());
  }, [openTasks, stickyPostDeny]);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const recordPosition = useRecordProjectPosition(projectId);
  const addNote = useAddProjectApprovalNote(projectId);
  const declineInvitation = useDeclineProjectInvitation(projectId);

  const clearStickyPostDeny = (versionId: string) => {
    setStickyPostDeny((prev) => {
      if (prev[versionId] == null) return prev;
      const next = { ...prev };
      delete next[versionId];
      return next;
    });
  };

  const assetsByVersionId = useMemo(
    () => buildApprovalAssetsByVersionId(tasks, reviews),
    [tasks, reviews],
  );

  const submit = async (input: {
    task: ParticipationTask;
    action: ApprovalRequestAction;
    noteBody?: string;
  }) => {
    const { task, action, noteBody } = input;
    setBusyVersionId(task.versionId);
    const idempotencyKey = `app:composer:${action}:${projectId}:${task.versionId}:${currentUserId ?? "me"}:${Date.now()}`;
    try {
      if (action === "not_needed") {
        await declineInvitation.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          ticketId: task.ticketId,
          idempotencyKey,
        });
        clearStickyPostDeny(task.versionId);
      } else if (action === "note_added") {
        const body = noteBody?.trim();
        if (!body) return;
        await addNote.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          noteBody: body,
          ticketId: task.ticketId,
          idempotencyKey,
        });
        clearStickyPostDeny(task.versionId);
      } else if (action === "changes_requested") {
        setStickyPostDeny((prev) => ({
          ...prev,
          [task.versionId]: task,
        }));
        await recordPosition.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          position: action,
          ticketId: task.ticketId,
          idempotencyKey,
        });
      } else {
        await recordPosition.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          position: action,
          ticketId: task.ticketId,
          idempotencyKey,
        });
        clearStickyPostDeny(task.versionId);
      }
    } catch {
      // If the request fails after we optimistically pinned the post-deny
      // note UI, clear it so the card doesn't stay stuck.
      clearStickyPostDeny(task.versionId);
    } finally {
      setBusyVersionId(null);
    }
  };

  return (
    <ApprovalRequestForm
      tasks={tasks}
      busyVersionId={busyVersionId}
      assetsByVersionId={assetsByVersionId}
      onCardHighlight={onCardHighlight}
      onPostDenySettled={clearStickyPostDeny}
      onRespond={(input) => {
        void submit(input);
      }}
    />
  );
}
